import { Text } from '@thanx/uikit/text'
import palette from 'constants/palette'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import { generateId } from 'utilities/uniqueId'

// Listbox drop-shadow — no palette/theme shadow token exists (mirrors
// RewardMultiSelect's DROPDOWN_SHADOW).
const DROPDOWN_SHADOW = '0 4px 8px rgba(0,0,0,0.1)'

// Listbox scroll region height (AI-1177 review: extracted since the search
// input row now sits above it, both inside the same popup).
const POPUP_MAX_HEIGHT_PX = '200px'

// Independent copy of RewardMultiSelect's resolveOptionBackground, kept
// decoupled rather than shared. Exported so its own regression test (the
// selected-wins-over-focused precedence) travels with the copy.
export const resolveOptionBackground = (
  isSelected: boolean,
  isFocused: boolean
): string => {
  if (isSelected) return merchantTheme.colors.belizeHole10
  if (isFocused) return merchantTheme.colors.grey05
  return 'white'
}

// Minimal row shape the component reads; richer caller types (e.g.
// RecoveryAI's LocationConfigRow) satisfy this structurally.
export interface LocationMultiSelectOption {
  id: number
  name: string
}

export interface LocationMultiSelectLabels {
  /** Bold field label above the trigger AND the listbox aria-label. */
  selectLocations: string
  /**
   * Already-count-resolved trigger text, e.g. "3 locations selected" /
   * "1 location selected". The CONSUMER picks the _one/_other form by count —
   * react-i18nify does not auto-pluralize, so this component never calls `t`.
   */
  locationsSelected: string
  /** Empty-state sentence shown when there are no locations to pick. */
  noLocationsAvailable: string
  /**
   * Placeholder + accessible name for the in-listbox search input (AI-1177).
   * A plain `role="searchbox"`, not the combobox's own typeahead — see the
   * component doc comment for how aria-activedescendant is shared with it.
   */
  searchLocations: string
  /** Empty-state sentence shown when the search filter matches nothing. */
  noLocationsMatch: string
}

export interface LocationMultiSelectProps {
  /** All selectable locations (the merchant's visible locations). */
  locations: LocationMultiSelectOption[]
  /** Controlled selection of enabled location ids (raw ints). */
  selectedIds: number[]
  /** Toggle a location in/out of the selection. */
  onToggle: (id: number) => void
  /** Pre-translated, namespace-owned strings (the component is i18n-agnostic). */
  labels: LocationMultiSelectLabels
  /** Optional stable id prefix for tests; defaults to a generateId()-based id. */
  idPrefix?: string
}

/**
 * Controlled, presentational location multi-select listbox. A verbatim mirror
 * of `RewardMultiSelect`'s hand-rolled WAI-ARIA combobox/listbox (custom
 * role="listbox"/role="option" + aria-activedescendant/aria-controls,
 * keyboard nav, outside-click + Escape focus-return) — NOT a uikit/baseui
 * Select. It differs from RewardMultiSelect only in carrying raw-int ids,
 * rendering locations (no cost column), and omitting the selected-chip list
 * (consumers render the removable rows via their own list component instead).
 *
 * AI-1177: adds a client-side text filter over the already-loaded `locations`
 * prop (no `search=` wire param — that's a separate, larger ticket). The
 * filter input is a plain, independently-focusable `role="searchbox"` rendered
 * inside the open popup, ABOVE the listbox — it is NOT the combobox's own
 * typeahead (the trigger keeps its own `role="combobox"`/aria-expanded/
 * aria-haspopup), but `aria-activedescendant` (+ aria-controls) is mirrored
 * onto it: once the search input can hold real DOM focus, the highlighted
 * option must be announced off WHICHEVER element currently has focus, not
 * just the trigger (review finding). Arrow-key/Enter navigation still works
 * while the input has real focus: `handleKeyDown` is bound on the shared
 * wrapping div, which every bubbled keydown (from either the trigger or the
 * search input) still reaches; Space is special-cased to type normally when
 * the search input holds focus (see the switch below) but still toggles the
 * focused option — without submitting a native click — while the trigger
 * holds focus, mirroring RewardMultiSelect.
 */
const LocationMultiSelect = ({
  locations,
  selectedIds,
  onToggle,
  labels,
  idPrefix,
}: LocationMultiSelectProps) => {
  const [css] = useStyletron()
  const [isOpen, setIsOpen] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const [searchQuery, setSearchQuery] = useState('')
  const [listboxId] = useState(
    () => `${idPrefix ?? `location-multiselect-${generateId()}`}-listbox`
  )
  const labelId = `${listboxId}-label`
  const triggerId = `${listboxId}-trigger`
  const searchInputId = `${listboxId}-search`
  const optionId = useCallback(
    (index: number) => `${listboxId}-option-${index}`,
    [listboxId]
  )
  const dropdownRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // O(1) membership checks in the option map (CLAUDE.md: pre-compute Sets).
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds])

  // Case-insensitive substring match against the already-loaded page of
  // locations (AI-1177) — no server round-trip. Untrimmed/empty query passes
  // every row through unfiltered.
  const filteredLocations = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return locations
    return locations.filter(location =>
      location.name.toLowerCase().includes(query)
    )
  }, [locations, searchQuery])

  // A narrowing filter can leave the previously-focused index pointing past
  // the new (shorter) list, or at a now-different row — reset to the first
  // filtered match (or -1 with none) whenever the filtered set changes.
  // Guarded on isOpen: this must NOT run while closed — every consumer of this
  // component mounts with the dropdown closed, and an unguarded reset would
  // flip focusedIndex 0 -> >=0 on mount, firing the scrollIntoView effect
  // below against a hidden (or, pre-open, not-yet-rendered) option.
  useEffect(() => {
    if (!isOpen) return
    setFocusedIndex(filteredLocations.length > 0 ? 0 : -1)
  }, [filteredLocations, isOpen])

  // Closing (Escape, outside-click, trigger click-to-close, Tab-out) always
  // clears the filter, so reopening starts from the full unfiltered list
  // rather than silently re-showing a stale, narrowed search.
  useEffect(() => {
    if (!isOpen) setSearchQuery('')
  }, [isOpen])

  // Outside click to close (returns focus to the trigger — WCAG 3.2.1).
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false)
        setFocusedIndex(-1)
        triggerRef.current?.focus()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen) {
        if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          setIsOpen(true)
          setFocusedIndex(0)
        }
        return
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          // Guarded (AI-1177 review): with zero filtered matches, focusedIndex
          // stays -1 rather than the clamp landing on the now-nonexistent 0
          // (which would point aria-activedescendant at a missing option).
          if (filteredLocations.length > 0) {
            setFocusedIndex(prev =>
              Math.min(prev + 1, filteredLocations.length - 1)
            )
          }
          break
        case 'ArrowUp':
          e.preventDefault()
          if (filteredLocations.length > 0) {
            setFocusedIndex(prev => Math.max(prev - 1, 0))
          }
          break
        case 'Enter':
        case ' ':
          // Space must NOT toggle/preventDefault while the search input holds
          // real DOM focus (AI-1177 review) — it needs Space to type a literal
          // space character ("Coffee Shop"). While the TRIGGER holds focus
          // (the common just-opened path), Space must stay intercepted here —
          // mirrors RewardMultiSelect's `case 'Enter': case ' ':` — or the
          // browser's native Space-activates-button fires on keyup and closes
          // the popup via the trigger's own onClick.
          if (e.key === ' ' && e.target === searchInputRef.current) {
            break
          }
          e.preventDefault()
          if (focusedIndex >= 0 && focusedIndex < filteredLocations.length) {
            onToggle(filteredLocations[focusedIndex].id)
          }
          break
        case 'Escape':
          e.preventDefault()
          setIsOpen(false)
          setFocusedIndex(-1)
          triggerRef.current?.focus()
          break
      }
    },
    [isOpen, focusedIndex, filteredLocations, onToggle]
  )

  // Tab-close: Tab from the trigger moves focus out of the dropdown (options are
  // non-focusable), so relatedTarget falls outside dropdownRef → close. Mirrors
  // RewardMultiSelect; does not return focus (Tab must move forward naturally).
  const handleBlur = useCallback((e: React.FocusEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(e.relatedTarget as Node)
    ) {
      setIsOpen(false)
      setFocusedIndex(-1)
    }
  }, [])

  // Scroll the focused option into view by its stable id (O(1)).
  useEffect(() => {
    if (focusedIndex >= 0) {
      document
        .getElementById(optionId(focusedIndex))
        ?.scrollIntoView({ block: 'nearest' })
    }
  }, [focusedIndex, optionId])

  // Shared by the trigger AND the search input (AI-1177 review): once the
  // search input can hold real DOM focus, aria-activedescendant must live on
  // WHICHEVER element the screen reader currently considers focused, or
  // arrowing through results updates an attribute nobody is listening to.
  const activeDescendant =
    isOpen && focusedIndex >= 0 ? optionId(focusedIndex) : undefined

  // With no locations to choose from, there is nothing to open — show the
  // empty-state sentence instead of an empty "0 locations selected" trigger.
  if (locations.length === 0) {
    return (
      <Text.BodyText4 color="grey70">
        {labels.noLocationsAvailable}
      </Text.BodyText4>
    )
  }

  return (
    <div className="d-flex flex-column">
      {/* <span id> because uikit <Text> does not forward `id` to the DOM.
          Visually hidden (compact-layout pass, AI-901 follow-up) — the
          trigger's own "N locations selected" text speaks for itself; this
          label lives on for the combobox's programmatic name (aria-labelledby
          below) and the listbox's aria-label. */}
      <span id={labelId} className="sr-only">
        <Text.BodyText4 bold>{labels.selectLocations}</Text.BodyText4>
      </span>
      <div
        ref={dropdownRef}
        className={css({ position: 'relative', marginTop: '8px' })}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
      >
        {/* Native <button> as the WAI-ARIA combobox/listbox TRIGGER (not a uikit
            Button CTA) — mirrors RewardMultiSelect: it must read as a neutral
            form field and carry hand-wired listbox ARIA
            (haspopup/expanded/controls/activedescendant). */}
        <button
          ref={triggerRef}
          id={triggerId}
          type="button"
          role="combobox"
          onClick={() => {
            if (isOpen) {
              setIsOpen(false)
              setFocusedIndex(-1)
            } else {
              setIsOpen(true)
              setFocusedIndex(0)
            }
          }}
          className={css({
            width: '100%',
            padding: '10px 16px',
            border: `1px solid ${merchantTheme.colors.grey30}`,
            borderRadius: '4px',
            backgroundColor: 'white',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            textAlign: 'left',
            fontFamily: 'Lato, sans-serif',
            fontSize: '16px',
            color: palette.grey70,
          })}
          aria-labelledby={labelId}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          aria-activedescendant={activeDescendant}
        >
          {labels.locationsSelected}
          <i
            className={`fa fa-chevron-${isOpen ? 'up' : 'down'}`}
            aria-hidden="true"
          />
        </button>

        {/* Always rendered; `hidden` removes it from the a11y tree when closed,
            so aria-controls always points at an existing node. `aria-hidden`
            alongside `hidden` makes the closed-listbox contract explicit for the
            WAI-ARIA 1.2 combobox pattern (older AT/browser combos may still
            traverse a CSS-only hidden node). Holds BOTH the search input
            (AI-1177) and the listbox — `hidden` on this shared wrapper keeps the
            search input out of the tab order while closed too. */}
        <div
          ref={listRef}
          hidden={!isOpen}
          aria-hidden={!isOpen}
          className={css({
            display: isOpen ? 'block' : 'none',
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            border: `1px solid ${merchantTheme.colors.grey30}`,
            borderRadius: '4px',
            backgroundColor: 'white',
            boxShadow: DROPDOWN_SHADOW,
            zIndex: 10,
          })}
        >
          {/* Plain, independently-focusable searchbox — NOT the combobox's own
              typeahead (see the component doc comment). A real <label> (not
              aria-label) gives it an accessible name without duplicating the
              placeholder text into a prop the consumer must also translate
              twice. */}
          <div
            className={css({
              padding: '8px',
              borderBottom: `1px solid ${merchantTheme.colors.grey30}`,
            })}
          >
            <label htmlFor={searchInputId} className="sr-only">
              {labels.searchLocations}
            </label>
            <input
              ref={searchInputRef}
              id={searchInputId}
              type="search"
              role="searchbox"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={labels.searchLocations}
              aria-controls={listboxId}
              aria-activedescendant={activeDescendant}
              className={css({
                width: '100%',
                padding: '6px 10px',
                border: `1px solid ${merchantTheme.colors.grey30}`,
                borderRadius: '4px',
                fontFamily: 'Lato, sans-serif',
                fontSize: '14px',
                color: palette.grey70,
              })}
            />
          </div>
          <div
            className={css({
              maxHeight: POPUP_MAX_HEIGHT_PX,
              overflowY: 'auto',
            })}
            id={listboxId}
            role="listbox"
            aria-label={labels.selectLocations}
            aria-multiselectable="true"
          >
            {filteredLocations.length === 0 ? (
              <div className={css({ padding: '10px 16px' })}>
                <Text.BodyText4 color="grey70">
                  {labels.noLocationsMatch}
                </Text.BodyText4>
              </div>
            ) : (
              filteredLocations.map((location, index) => {
                const isSelected = selectedIdSet.has(location.id)
                const isFocused = index === focusedIndex
                return (
                  // Non-focusable role="option" div (no tabindex), mirroring
                  // RewardMultiSelect — a mouse click never moves DOM focus off
                  // whichever control (trigger or search input) currently holds
                  // it; onMouseDown preventDefault stops that click from
                  // blurring it so the click toggles before blur closes.
                  <div
                    key={location.id}
                    id={optionId(index)}
                    role="option"
                    aria-selected={isSelected}
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => {
                      onToggle(location.id)
                      setFocusedIndex(index)
                    }}
                    className={css({
                      width: '100%',
                      padding: '10px 16px',
                      backgroundColor: resolveOptionBackground(
                        isSelected,
                        isFocused
                      ),
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      textAlign: 'left',
                      fontFamily: 'Lato, sans-serif',
                      fontSize: '14px',
                      outline: isFocused
                        ? `2px solid ${merchantTheme.colors.belizeHole50}`
                        : 'none',
                      outlineOffset: '-2px',
                    })}
                  >
                    <span>{location.name}</span>
                  </div>
                )
              })
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default LocationMultiSelect
